import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { CAT_CONFIG } from '../lib/data';

const WARSAW = { lat: 52.233, lng: 21.021 };

// User dot icon
const userDotHTML = `
  <div style="width:20px;height:20px;border-radius:99px;background:rgba(59,130,246,.25);display:flex;align-items:center;justify-content:center;animation:pulse 2s ease-out infinite">
    <div style="width:10px;height:10px;border-radius:99px;background:#3B82F6;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3)"></div>
  </div>`;

export default function MapView({ filtered, selected, onPinClick, userLat, userLng }) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const markersRef   = useRef({});
  const userMarkerRef = useRef(null);
  const userCircleRef = useRef(null);

  // Init map once
  useEffect(() => {
    if (mapRef.current) return;
    mapRef.current = L.map(containerRef.current, {
      center: [WARSAW.lat, WARSAW.lng],
      zoom: 14,
      zoomControl: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(mapRef.current);
    L.control.zoom({ position: 'bottomright' }).addTo(mapRef.current);
  }, []);

  // Update user location dot
  useEffect(() => {
    const map = mapRef.current;
    if (!map || userLat === null) return;

    const dotIcon = L.divIcon({
      html: userDotHTML,
      className: '',
      iconAnchor: [10, 10],
      iconSize: [20, 20],
    });

    if (userMarkerRef.current) {
      userMarkerRef.current.setLatLng([userLat, userLng]);
    } else {
      userMarkerRef.current = L.marker([userLat, userLng], {
        icon: dotIcon,
        zIndexOffset: 1000,
      }).addTo(map);
    }

    if (userCircleRef.current) {
      userCircleRef.current.setLatLng([userLat, userLng]);
    } else {
      userCircleRef.current = L.circle([userLat, userLng], {
        radius: 80,
        color: '#3B82F6',
        fillColor: '#3B82F6',
        fillOpacity: 0.08,
        weight: 1,
      }).addTo(map);
    }

    map.setView([userLat, userLng], 14, { animate: true });
  }, [userLat, userLng]);

  // Rebuild activity markers when filtered set or selection changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    Object.values(markersRef.current).forEach(m => m.remove());
    markersRef.current = {};

    filtered.forEach(activity => {
      const cat    = CAT_CONFIG[activity.category] || CAT_CONFIG.entertainment;
      const isSel  = selected && selected.id === activity.id;
      const size   = isSel ? 38 : 32;
      const html   = `
        <div style="display:flex;flex-direction:column;align-items:center;cursor:pointer;transition:all .15s">
          <div style="
            width:${size}px;height:${size}px;border-radius:99px;
            background:${cat.color};
            display:flex;align-items:center;justify-content:center;
            font-size:${isSel ? 18 : 15}px;
            box-shadow:${isSel
              ? `0 0 0 3px #fff, 0 4px 16px rgba(0,0,0,.35)`
              : '0 2px 8px rgba(0,0,0,.25)'};
            transform:${isSel ? 'scale(1.1)' : 'scale(1)'};
            transition:all .15s;
          ">${cat.emoji}</div>
          <div style="width:2px;height:6px;background:${cat.color};border-radius:99px;margin-top:1px"></div>
          <div style="width:5px;height:5px;border-radius:99px;background:rgba(0,0,0,.15)"></div>
        </div>`;

      const icon = L.divIcon({
        html,
        className: '',
        iconAnchor: [isSel ? 19 : 16, isSel ? 50 : 45],
        iconSize: [null, isSel ? 50 : 45],
      });

      const marker = L.marker([activity.lat, activity.lng], { icon }).addTo(map);
      marker.on('click', () => onPinClick(activity));
      markersRef.current[activity.id] = marker;
    });
  }, [filtered, selected, onPinClick]);

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

      {/* Pulse animation for user dot */}
      <style>{`
        @keyframes pulse {
          0%   { box-shadow: 0 0 0 0   rgba(59,130,246,.4); }
          70%  { box-shadow: 0 0 0 12px rgba(59,130,246,0);  }
          100% { box-shadow: 0 0 0 0   rgba(59,130,246,0);   }
        }
        .leaflet-control-zoom { z-index: 400 !important; }
        .leaflet-control-container { z-index: 400 !important; }          
      `}</style>

      {/* Result count pill */}
      <div style={{
        position: 'absolute', top: 11, left: '50%', transform: 'translateX(-50%)',
        zIndex: 10, background: '#fff', borderRadius: 99, padding: '5px 13px',
        fontSize: 12, fontWeight: 700, color: '#555',
        boxShadow: '0 2px 10px rgba(0,0,0,.12)', whiteSpace: 'nowrap',
        pointerEvents: 'none',
      }}>
        {filtered.length === 0
          ? 'Brak wyników'
          : `${filtered.length} ${filtered.length === 1 ? 'miejsce' : filtered.length < 5 ? 'miejsca' : 'miejsc'} na mapie`}
      </div>

      {/* Locate button */}
      <button
        onClick={() => {
          if (!navigator.geolocation) return;
          navigator.geolocation.getCurrentPosition(
            pos => {
              const e = new CustomEvent('kidzmap:location', { detail: pos.coords });
              window.dispatchEvent(e);
            },
            () => {}
          );
        }}
        style={{
          position: 'absolute', bottom: 90, right: 14, zIndex: 10,
          width: 40, height: 40, borderRadius: 99, border: 'none',
          background: '#fff', boxShadow: '0 2px 10px rgba(0,0,0,.18)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, cursor: 'pointer',
        }}
        title="Moja lokalizacja"
      >
        📍
      </button>
    </div>
  );
}